import { ICartSearchItemClickResponseData } from "@/api_framework/api_modals/Cart";
import SimpleCheckbox from "@/app/components/ui/CheckBoxes/SimpleCheckbox";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useUtils } from "@/app/hooks/useUtils";
import { toNumber } from "lodash";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import clsx from "clsx";
import { ICartTableFormPetItem } from "../../auth_routes/CartManagement/CartCreationTableComponent";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";

export interface ICartItemAddModalState {
  onItemsAdd: (items: ICartSearchItemClickResponseData[]) => void;
  cartVariants: ICartSearchItemClickResponseData[];
  source?: "cart" | "clinic_to_doctor_transfer";
}

export interface ICartItemsAddForm {
  cartItems: ICartSearchItemClickResponseData[];
}

const CartItemAddForm: React.FC<{
  itemVariants: ICartSearchItemClickResponseData[];
  selected: ICartSearchItemClickResponseData[];
  setSelected: Dispatch<SetStateAction<ICartSearchItemClickResponseData[]>>;
  source?: "cart" | "clinic_to_doctor_transfer";
}> = ({ itemVariants, selected, setSelected, source = "cart" }) => {
  const isClinicToDoctor = source === "clinic_to_doctor_transfer";
  const { getRouteKey } = useRouter();
  const [carts, setCarts] = useState<ICartTableFormPetItem[]>([]);
  const [doctorPrescribedQty, setDoctorPrescribedQty] = useState(0);

  const { activeCartDetails } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id"))?.routeState?.state as ICartState;

  useEffect(() => {
    if (activeCartDetails) {
      setCarts(
        (activeCartDetails?.pets ?? []).map((pet) => {
          return {
            ...pet,
            items: (pet?.items ?? []).map((item) => {
              return {
                ...item,
                row_id: item?.id,
                all_applicable_coupons: item?.all_applicable_coupons,
              };
            }),
          };
        }),
      );
    }
  }, [activeCartDetails]);

  const { veticDateFormat } = useUtils();
  const mappedItemVariants = itemVariants?.map((iv) => {
    return {
      ...iv,
      variant_id: iv.id,
    };
  });

  const formHook = useForm<ICartItemsAddForm>({
    defaultValues: {
      cartItems: mappedItemVariants,
    },
  });

  const formArrayHook = useFieldArray<ICartItemsAddForm>({
    name: "cartItems",
    control: formHook.control,
  });

  const isSelected = (urid: string) => {
    const item = selected?.find((s) => s?.unique_row_id === urid);
    return item !== undefined;
  };

  const validateCartItemQuantity = (
    rowIndex: number,
    cartItemQuantity: number,
    maxQuantity: number,
    row_id: string,
  ) => {
    if (cartItemQuantity === 0) {
      setSelected((prev) => {
        return (prev ?? []).filter((p) => p?.unique_row_id !== row_id);
      });
      // formHook.setError(`cartItems.${rowIndex}.qty`, {
      //   message: `Invalid Qty`,
      // });
    } else if (cartItemQuantity > maxQuantity) {
      formHook.setError(`cartItems.${rowIndex}.qty`, {
        message: `Invalid Qty`,
      });
    } else if (cartItemQuantity % 1 !== 0) {
      formHook.setError(`cartItems.${rowIndex}.qty`, {
        message: "Invalid Qty",
      });
    } else {
      setSelected((prev) => {
        return [
          ...(prev ?? []).filter((item) => item?.unique_row_id !== row_id), // Remove the existing item with matching row_id
          formHook.getValues(`cartItems.${rowIndex}`), // Add the updated item
        ];
      });

      formHook.clearErrors(`cartItems.${rowIndex}.qty`);
    }
  };
  useEffect(() => {
    if (carts?.length > 0 && formArrayHook?.fields?.length > 0) {
      const items = (carts ?? []).flatMap((pet) => pet?.items ?? []).filter(Boolean);

      const filteredData = items.filter((item1) =>
        formArrayHook?.fields?.some(
          (item2) =>
            item2?.variant_id === item1?.id &&
            item2?.lot_details?.vetbuddy_lot_number === item1?.lot_details?.vetbuddy_lot_number &&
            item2?.lot_details?.expiry_date === item1?.lot_details?.expiry_date &&
            item2?.lot_details?.vetbuddy_manufacturer_name === item1?.lot_details?.vetbuddy_manufacturer_name &&
            item2?.lot_details?.vetbuddy_mrp === item1?.lot_details?.vetbuddy_mrp,
        ),
      );

      const totalQty = (filteredData ?? [])
        .filter((item) => item?.source === 2) // Filter items where source is 2
        .reduce((sum, item) => sum + (item?.qty ?? 0), 0);

      setDoctorPrescribedQty(totalQty);

      formArrayHook.fields.forEach((field, index) => {
        const match = filteredData?.find(
          (item1) =>
            item1?.id === field?.variant_id &&
            item1?.lot_details?.vetbuddy_lot_number === field?.lot_details?.vetbuddy_lot_number &&
            item1?.lot_details?.expiry_date === field?.lot_details?.expiry_date &&
            item1?.lot_details?.vetbuddy_manufacturer_name === field?.lot_details?.vetbuddy_manufacturer_name &&
            item1?.lot_details?.vetbuddy_mrp === field?.lot_details?.vetbuddy_mrp,
        );

        if (match) {
          formArrayHook.update(index, { ...field, qty: match.qty });
        }
      });
    }
  }, [formArrayHook?.fields?.length > 0, carts]);

  useEffect(() => {
    const data = formHook.watch("cartItems");
    if (data?.length > 0) {
      const filteredData = data.filter((item) => (item?.qty ?? 0) > 0);
      setSelected(filteredData);
    }
  }, [formHook.watch("cartItems")]);

  const handelRowClick = (index: any, row_id: any) => {
    setSelected((prev) => {
      const mm = (prev ?? []).find((i) => i?.unique_row_id === row_id);
      if (mm?.unique_row_id) {
        return (prev ?? []).filter((p) => p?.unique_row_id !== row_id);
      } else {
        return [...(prev ?? []), formHook.getValues(`cartItems.${index}`)];
      }
    });
    //
  };

  const handelCheckExpiry = (index: any) => {
    const fff = formHook.getValues(`cartItems.${index}`);
    const expiry_date = fff?.lot_details?.expiry_date;

    if (expiry_date) {
      const daysThreshold = 15;
      const today = new Date();

      // Convert "DD/MM/YYYY" to "YYYY-MM-DD"
      const expiryParts = expiry_date?.split("/");
      if (expiryParts?.length === 3) {
        const expiry = new Date(`${expiryParts[2]}-${expiryParts[1]}-${expiryParts[0]}`);

        const timeDifference = expiry.getTime() - today.getTime(); // Convert both to timestamps
        const daysDifference = timeDifference / (1000 * 60 * 60 * 24); // Convert ms to days

        if (daysDifference <= daysThreshold) {
          return true;
        } else {
          return false;
        }
      } else {
        console.error("Invalid expiry date format:", expiry_date);
        return false;
      }
    } else {
      return false;
    }
  };

  return (
    formArrayHook?.fields?.length > 0 && (
      <>
        {doctorPrescribedQty > 0 && (
          <p className=" text-xs text-red-600 font-medium">Prescribed Qty:{doctorPrescribedQty}</p>
        )}
        <div className="mt-2 2xl:mt-8 flow-root">
          <div className="-mx-4 sm:-mx-6 lg:-mx-8 -my-2 overflow-x-auto">
            <div className="inline-block sm:px-6 lg:px-8 py-2 min-w-full align-middle">
              <div className="ring-opacity-5 shadow ring-black overflow-hidden rounded-xl bg-white">
                <table className="min-w-full border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23]">
                    <tr>
                      {/* Checkbox Field */}
                      <th
                        scope="col"
                        className="py-2.5 pr-3 pl-4 sm:pl-6 font-medium text-black text-left text-xs"
                      ></th>
                      <th
                        scope="col"
                        className="px-3 py-2.5 2xl:pr-3 2xl:pl-4 sm:pl-6 font-medium text-black text-left text-xs"
                      >
                        Product Name
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium text-black text-left text-xs ">
                        Lot
                      </th>
                      {!isClinicToDoctor && (
                        <th scope="col" className="px-3 py-2.5 font-medium text-black text-left text-xs">
                          Manufacturer
                        </th>
                      )}
                      <th scope="col" className="px-3 py-2.5 font-medium text-black text-left text-xs">
                        Expiry
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium text-black text-left text-xs">
                        MRP
                      </th>
                      <th
                        scope="col"
                        className={clsx(
                          "px-3 py-2.5 font-medium text-black text-xs",
                          isClinicToDoctor ? "text-center" : "text-left",
                        )}
                      >
                        On-Hand Qty
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium text-black text-left text-xs">
                        Add Qty
                      </th>
                    </tr>
                  </thead>
                  <tbody className="overflow-hidden rounded-xl w-full">
                    {formArrayHook.fields?.map((field, index) => (
                      <tr
                        key={field.id}
                        className={clsx(
                          "cursor-pointer bg-white transition",
                          formArrayHook.fields.length === 1 && "rounded-xl",
                          index === 0 && formArrayHook.fields.length > 1 && "rounded-t-xl",
                          index === formArrayHook.fields.length - 1 && formArrayHook.fields.length > 1 && "rounded-b-xl",
                        )}
                      >
                        <td
                          className={clsx(
                            "py-2.5 pr-3 pl-4 sm:pl-6 text-black text-sm whitespace-nowrap",
                            formArrayHook.fields.length === 1 && "rounded-tl-2xl rounded-bl-2xl",
                            index === 0 && formArrayHook.fields.length > 1 && "rounded-tl-2xl",
                            index === formArrayHook.fields.length - 1 &&
                              formArrayHook.fields.length > 1 &&
                              "rounded-bl-2xl",
                          )}
                        >
                          <SimpleCheckbox
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelected((prev) => {
                                  return [...prev, formHook.getValues(`cartItems.${index}`)];
                                });
                              } else {
                                setSelected((prev) => {
                                  return prev.filter((p) => p.unique_row_id !== field.unique_row_id);
                                });
                              }
                            }}
                            // checked={formHook.watch("lotNumber")}
                            checked={isSelected(field.unique_row_id)}
                            id="LOTNumber"
                            colorClassName="text-[#202223] cursor-pointer"
                          />
                          {/* <input
                          type="checkbox"
                          checked={isSelected(field.unique_row_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelected((prev) => {
                                return [...prev, formHook.getValues(`cartItems.${index}`)];
                              });
                            } else {
                              setSelected((prev) => {
                                return prev.filter((p) => p.unique_row_id !== field.unique_row_id);
                              });
                            }
                          }}
                        /> */}
                        </td>
                        <td
                          className="py-2.5 pr-3 pl-4 sm:pl-6 text-black text-xs break-words w-1/3"
                          onClick={() => handelRowClick(index, field.unique_row_id)}
                        >
                          {field?.name}
                        </td>
                        <td
                          className="px-3 py-2.5 text-black text-xs whitespace-nowrap"
                          onClick={() => handelRowClick(index, field.unique_row_id)}
                        >
                          {field?.lot_details?.vetbuddy_lot_number}
                        </td>
                      {!isClinicToDoctor && (
                          <td
                            className="px-3 py-2.5 text-black text-xs whitespace-nowrap"
                            onClick={() => handelRowClick(index, field.unique_row_id)}
                          >
                            {field?.lot_details?.vetbuddy_manufacturer_name}
                          </td>
                        )}
                        <td
                          className={`px-3 py-2.5 text-xs whitespace-nowrap ${handelCheckExpiry(index) ? (isClinicToDoctor ? "text-red-600" : "text-[#069747]") : "text-black"}`}
                          onClick={() => handelRowClick(index, field.unique_row_id)}
                        >
                          {veticDateFormat(field?.lot_details?.expiry_date ?? null)}
                        </td>
                        <td
                          className="px-3 py-2.5 text-black text-xs whitespace-nowrap"
                          onClick={() => handelRowClick(index, field.unique_row_id)}
                        >
                          {field?.lot_details?.vetbuddy_mrp}
                        </td>
                        <td
                          className="px-3 py-2.5 text-black text-xs whitespace-nowrap"
                          onClick={() => handelRowClick(index, field.unique_row_id)}
                        >
                            <div className={clsx("flex items-center gap-3", isClinicToDoctor && "justify-center")}>
                            {field?.in_hand_qty}
                            {handelCheckExpiry(index) && (
                              <span className={clsx("px-2 py-0.5 rounded-md text-xs font-medium border", isClinicToDoctor ? "text-red-600 bg-[#FFEBEB] border-red-200" : "text-[#069747] bg-[#ECFAF2] border-[#069747]")}>
                                {isClinicToDoctor ? "Expiring Soon" : "Close to Expiry"}
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          className={clsx(
                            "px-3 py-2.5 text-black text-xs whitespace-nowrap",
                            formArrayHook.fields.length === 1 && "rounded-tr-2xl rounded-br-2xl",
                            index === 0 && formArrayHook.fields.length > 1 && "rounded-tr-2xl",
                            index === formArrayHook.fields.length - 1 &&
                              formArrayHook.fields.length > 1 &&
                              "rounded-br-2xl",
                          )}
                        >
                          <div className="relative">
                            <InputWithStartAndEndAddOn
                              addonWrapperClassName="mt-0"
                              startAddon="-"
                              startAddOnCallback={() => {
                                if (formHook.watch(`cartItems.${index}.qty`) > 0) {
                                  formHook.setValue(
                                    `cartItems.${index}.qty`,
                                    formHook.watch(`cartItems.${index}.qty`) - 1,
                                  );
                                  validateCartItemQuantity(
                                    index,
                                    toNumber(formHook.watch(`cartItems.${index}.qty`)),
                                    field.in_hand_qty,
                                    field.unique_row_id,
                                  );
                                }
                              }}
                              step={1}
                              endAddOnCallback={() => {
                                if (formHook.watch(`cartItems.${index}.qty`) === field.in_hand_qty) {
                                  formHook.setError(`cartItems.${index}.qty`, {
                                    message: `Max Qty Reached`,
                                  });
                                  return;
                                }
                                formHook.setValue(
                                  `cartItems.${index}.qty`,
                                  formHook.watch(`cartItems.${index}.qty`) + 1,
                                );
                                validateCartItemQuantity(
                                  index,
                                  toNumber(formHook.watch(`cartItems.${index}.qty`)),
                                  field.in_hand_qty,
                                  field.unique_row_id,
                                );
                              }}
                              min={1}
                              max={field.in_hand_qty}
                              endAddon="+"
                              value={formHook.watch(`cartItems.${index}.qty`)}
                              inputClassName="py-1"
                              onBlur={(e) => {
                                validateCartItemQuantity(
                                  index,
                                  toNumber(e.target.value),
                                  field.in_hand_qty,
                                  field.unique_row_id,
                                );
                              }}
                              onChange={(e: any) => {
                                const regex = /^[0-9]*$/;
                                if (toNumber(e.target.value) > field.in_hand_qty) {
                                  formHook.setValue(`cartItems.${index}.qty`, field.in_hand_qty);
                                  return;
                                }
                                if (!regex.test(e.target.value)) {
                                  formHook.setValue(`cartItems.${index}.qty`, formHook.watch(`cartItems.${index}.qty`));
                                } else {
                                  formHook.setValue(`cartItems.${index}.qty`, toNumber(e.target.value));
                                }
                              }}
                              type="string"
                            />
                            <span className="text-red-500 absolute  text-[10px] whitespace-normal">
                              {" "}
                              {formHook.formState.errors.cartItems?.[index]?.qty?.message}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  );
};

const CartItemAddModal = () => {
  const { setInvisible } = useModalActions("CART_ITEM_ADD_MODAL");
  const { onItemsAdd, cartVariants, source } =
    (useModalState("CART_ITEM_ADD_MODAL") as ICartItemAddModalState) ?? ({} as Partial<ICartItemAddModalState>);

  const [selected, setSelected] = useState<ICartSearchItemClickResponseData[]>([]);

  const validateSelectedItemsQty = () => {
    if (!selected || selected.length === 0) {
      return false;
    }

    for (let i = 0; i < selected.length; i++) {
      const qty = toNumber(selected[i]?.qty);
      if (qty <= 0 || qty > (selected[i]?.in_hand_qty ?? 0)) {
        return false;
      }
    }

    return true;
  };

  return (
    <ModalPrompt
      persistent
      disableInitialFocus
      modalId="CART_ITEM_ADD_MODAL"
      dialogClassName="sm:my-8 sm:w-full sm:max-w-6xl sm:p-6 overflow-hidden"
      button={{
        primaryButtonText: "Add Items",
        secondaryButtonText: "Cancel",
        primaryButtonClassname: "bg-[#000000] text-white",
        primaryCallback: () => {
          if (validateSelectedItemsQty()) {
            onItemsAdd?.(selected);
            setSelected([]);
            setInvisible();
          }
        },
        primaryButtonDisabled: !validateSelectedItemsQty(),

        secondaryCallback: () => {
          setSelected([]);
          setInvisible();
        },

        shouldHideOnClick: false,
      }}
      title="Add Lot Details"
      content={
        <CartItemAddForm
          selected={selected}
          setSelected={setSelected}
          itemVariants={cartVariants}
          source={source}
        />
      }
    />
  );
};

export default CartItemAddModal;
